import { useEffect, useMemo, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import {
  Badge, Button, Card, Select, SelectItem, Table, TableBody, TableCell, TableHead, TableHeaderCell, TableRow, Text, TextInput, Title,
} from '@tremor/react';
import DateRangePresets from '../components/DateRangePresets';
import EmptyState from '../components/EmptyState';
import ErrorRetry from '../components/ErrorRetry';
import TableSkeleton from '../components/TableSkeleton';
import { useDrilldown } from '../components/DrilldownProvider';
import { fetchPurchases } from '../api/purchasing';
import { useStoreFilter } from '../lib/storeFilter';
import { usePagination } from '../lib/usePagination';
import Pagination from '../components/Pagination';
import { localYMD } from '../lib/format';
import { exportCsv } from '../lib/csv';
import { exportReportPdf } from '../lib/pdf';
import { t } from '../lib/i18n';
import type { Purchase, User } from '../api/types';

const fmtMxn = new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN', minimumFractionDigits: 2 });
const fmtQty = new Intl.NumberFormat('es-MX', { maximumFractionDigits: 2 });

function dateNDaysAgo(n: number): string {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return localYMD(d);
}

function statusBadge(s: Purchase['status']) {
  const color = s === 'closed' ? 'emerald' : s === 'partial' ? 'amber' : 'gray';
  return <Badge color={color}>{t.purchasing.purchases.status[s]}</Badge>;
}

export default function PurchasingPurchases() {
  const user = useOutletContext<User>();
  const [from, setFrom] = useState(dateNDaysAgo(30));
  const [to, setTo] = useState(dateNDaysAgo(0));
  const [supId, setSupId] = useState('');
  const [status, setStatus] = useState<'all' | 'open' | 'partial' | 'closed'>('all');
  // Shared header store (lib/storeFilter); PurchasingLayout renders the picker.
  const [sid] = useStoreFilter(user);
  const [rows, setRows] = useState<Purchase[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  // Order detail comes from the app-wide drill-down stack (JCD-59).
  const { open: openDrill } = useDrilldown();
  const pg = usePagination(rows);

  const load = () => {
    setErr(null);
    setRows(null);
    fetchPurchases({ from, to, sid, sup_id: supId || undefined, status })
      .then(setRows)
      .catch((e: Error) => setErr(e.message ?? String(e)));
  };
  useEffect(() => { load(); }, [from, to, supId, status, sid]);

  const totalCost = useMemo(
    () => (rows ?? []).reduce((acc, r) => acc + r.total_cost_mxn, 0),
    [rows],
  );

  // Export the full filtered set (rows), not pg.pageRows — see usePagination's
  // contract: "CSV/PDF exports keep the full set."
  const exportCols = t.purchasing.purchases.cols;
  const exportColumns = [
    exportCols.date, exportCols.onum, exportCols.sup, exportCols.store,
    exportCols.lines, exportCols.qtyOrd, exportCols.qtyRcv, exportCols.cost,
    exportCols.status, exportCols.lastRecv,
  ];
  const exportRows = (rows ?? []).map((r): Array<string | number> => [
    r.cdate, r.onum, `${r.sup_id} · ${r.sup_name}`, r.sid,
    r.lines, r.qty_ord, r.qty_rcv, r.total_cost_mxn,
    t.purchasing.purchases.status[r.status], r.last_recv_at ?? '—',
  ]);

  function downloadCsv() {
    if (!rows || rows.length === 0) return;
    exportCsv({ columns: exportColumns, rows: exportRows, filename: `ordenes_compra_${from}_${to}.csv` });
  }

  function downloadPdf() {
    if (!rows || rows.length === 0) return;
    exportReportPdf({
      title: t.purchasing.purchases.title,
      meta: [`${from} – ${to}`],
      printedBy: user.username,
      columns: exportColumns,
      rows: exportRows,
      numericColumns: [1, 4, 5, 6, 7],
      orientation: 'landscape',
      filename: `ordenes_compra_${from}_${to}.pdf`,
    });
  }

  return (
    <div className="space-y-4">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <Title>{t.purchasing.purchases.title}</Title>
          <Text className="text-tremor-content text-sm">{t.purchasing.purchases.subtitle}</Text>
        </div>
      </div>

      <Card className="space-y-3">
        <DateRangePresets from={from} to={to} onChange={({ from: f, to: t2 }) => { setFrom(f); setTo(t2); }} />
        <div className="flex flex-wrap gap-3 items-end">
          <label className="text-sm">
            <div className="text-tremor-content text-xs mb-1">Proveedor (ID)</div>
            <TextInput value={supId} onValueChange={setSupId} placeholder="ABC" className="w-40" />
          </label>
          <label className="text-sm">
            <div className="text-tremor-content text-xs mb-1">{t.purchasing.purchases.cols.status}</div>
            <Select value={status} onValueChange={(v) => setStatus(v as typeof status)} className="w-40">
              <SelectItem value="all">{t.purchasing.purchases.status.all}</SelectItem>
              <SelectItem value="open">{t.purchasing.purchases.status.open}</SelectItem>
              <SelectItem value="partial">{t.purchasing.purchases.status.partial}</SelectItem>
              <SelectItem value="closed">{t.purchasing.purchases.status.closed}</SelectItem>
            </Select>
          </label>
          <div className="ml-auto flex items-center gap-3">
            <Text className="text-sm text-tremor-content">
              {rows?.length ?? 0} OC · Total {fmtMxn.format(totalCost)}
            </Text>
            <Button size="xs" variant="secondary" disabled={!rows || rows.length === 0} onClick={downloadCsv}>
              {t.purchasing.purchases.export.csv}
            </Button>
            <Button size="xs" variant="secondary" disabled={!rows || rows.length === 0} onClick={downloadPdf}>
              {t.purchasing.purchases.export.pdf}
            </Button>
          </div>
        </div>
      </Card>

      {err && <ErrorRetry message={err} onRetry={load} />}
      {!err && rows === null && <TableSkeleton rows={6} cols={9} />}
      {!err && rows !== null && rows.length === 0 && <EmptyState message={t.purchasing.purchases.empty} />}
      {!err && rows !== null && rows.length > 0 && (
        <Card className="p-0 overflow-hidden">
          <Table>
            <TableHead>
              <TableRow>
                <TableHeaderCell>{t.purchasing.purchases.cols.date}</TableHeaderCell>
                <TableHeaderCell>{t.purchasing.purchases.cols.onum}</TableHeaderCell>
                <TableHeaderCell>{t.purchasing.purchases.cols.sup}</TableHeaderCell>
                <TableHeaderCell>{t.purchasing.purchases.cols.store}</TableHeaderCell>
                <TableHeaderCell className="text-right">{t.purchasing.purchases.cols.lines}</TableHeaderCell>
                <TableHeaderCell className="text-right">{t.purchasing.purchases.cols.qtyOrd}</TableHeaderCell>
                <TableHeaderCell className="text-right">{t.purchasing.purchases.cols.qtyRcv}</TableHeaderCell>
                <TableHeaderCell className="text-right">{t.purchasing.purchases.cols.cost}</TableHeaderCell>
                <TableHeaderCell>{t.purchasing.purchases.cols.status}</TableHeaderCell>
                <TableHeaderCell>{t.purchasing.purchases.cols.lastRecv}</TableHeaderCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {pg.pageRows.map((r) => (
                <TableRow
                  key={`${r.sid}-${r.onum}`}
                  className="cursor-pointer hover:bg-tremor-background-muted"
                  onClick={() => openDrill({ kind: 'order', sid: r.sid, onum: r.onum })}
                >
                  <TableCell>{r.cdate}</TableCell>
                  <TableCell>{r.onum}</TableCell>
                  <TableCell>{r.sup_id} · {r.sup_name}</TableCell>
                  <TableCell>{r.sid}</TableCell>
                  <TableCell className="text-right">{r.lines}</TableCell>
                  <TableCell className="text-right">{fmtQty.format(r.qty_ord)}</TableCell>
                  <TableCell className="text-right">{fmtQty.format(r.qty_rcv)}</TableCell>
                  <TableCell className="text-right">{fmtMxn.format(r.total_cost_mxn)}</TableCell>
                  <TableCell>{statusBadge(r.status)}</TableCell>
                  <TableCell>{r.last_recv_at ?? '—'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <Pagination {...pg} className="px-4" />
        </Card>
      )}

    </div>
  );
}
